import React, { useContext, useState } from 'react';
import i18next from 'i18next';
import { ColumnDefinition, Input, Pills, RegularTitle, RowDefinition, Table, Tabs } from '@indigo-ag/component-module';
import { AGFCustomFilters, AgFinancing, AgFinancingConnection } from '@indigo-ag/schema';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import debounce from 'lodash/debounce';
import { Link } from 'react-router-dom';

import { NewPage } from 'core/components';
import { brazil, date, sessionStorageUtils, text } from 'shared/utils';
import { CopyToClipboardButton, LoadingWrapper, createStylesHook, Permissions } from 'shared';
import { financingAgFinancingQueue } from 'features/brazil-grower/queries';
import { PillIds, PILLS_CONFIG, TabIds, TABS_CONFIG } from 'features/brazil-grower/pages/tabs/config';
import { UserContext } from 'core/components/GateWay/GateWay';

interface FormattedReport {
  id: string;
  name: string;
  date: Date | undefined;
  state: string;
  cpfOrCnpj: string;
  salesPerson: string;
  campaign: string;
}

const styles = createStylesHook(() => ({
  pillsLayout: {
    '& button[role="radio"]': {
      marginTop: '32px'
    }
  }
}));

type ColumnId = 'name' | 'date' | 'state' | 'cpfOrCnpj' | 'salesPerson' | 'campaign';
const columnDefinition: ColumnDefinition<FormattedReport, ColumnId>[] = [
  {
    id: 'name',
    header: i18next.t('name'),
    cell: ({ row }) => (
      <Link
        to={`/br-growers/${row.id}`}
        onClick={() => sessionStorageUtils.set('negotiationBackLink', window.location.pathname)}
      >
        <RegularTitle style={{ fontWeight: 400, color: 'inherit' }}>{text.toTitleCase(row.name)}</RegularTitle>
      </Link>
    )
  },
  {
    id: 'date',
    header: i18next.t('date'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={`${date.formatDate(row.date)}`} />
  },
  { id: 'state', header: i18next.t('state'), cell: ({ row, SimpleCell }) => <SimpleCell label={row.state} /> },
  {
    id: 'cpfOrCnpj',
    header: i18next.t('cpfNumber'),
    cell: ({ row, SimpleCell }) => {
      const unformattedCpfOrCnpj = brazil.cleanCpfCnpjValue(row.cpfOrCnpj);
      return (
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <SimpleCell label={row.cpfOrCnpj} />
          <CopyToClipboardButton valueToCopy={unformattedCpfOrCnpj} />
        </div>
      );
    }
  },
  {
    id: 'salesPerson',
    header: i18next.t('salesPerson'),
    cell: ({ row, SimpleCell }) => <SimpleCell label={row.salesPerson} />
  },
  { id: 'campaign', header: i18next.t('campaign'), cell: ({ row, SimpleCell }) => <SimpleCell label={row.campaign} /> }
];

const rowDefinition: RowDefinition<FormattedReport, ColumnId> = {
  rowId: ({ row }) => row.id
};

const getCurrentColumns = (isTerramagna: boolean): ColumnId[] => {
  const currentColumns: ColumnId[] = ['name', 'date'];
  currentColumns.push('state', 'cpfOrCnpj');
  if (!isTerramagna) {
    currentColumns.push('salesPerson', 'campaign');
  }
  return currentColumns;
};

export const Reports: React.FunctionComponent = () => {
  /**
   * styles
   */
  const classes = styles();

  const rowsPerPageOptions = [10, 25, 50];
  /**
   * State
   */
  const [activeTabId, setActiveTab] = useState(TabIds.CREDIT_LEAD_REPORT_INITIAL_ANALYSIS);
  const [activePill, setActivePill] = useState<PillIds | null>(PillIds.BACKGROUND_CHECK_PENDING);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(rowsPerPageOptions[1]);
  const [searchText, setSearchText] = useState('');

  /**
   * User Context
   */
  const { hasPermission } = useContext(UserContext);
  const isTerramagna = hasPermission(Permissions.FA_EXTERNAL_FIELD_MONITORING);

  const handleSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearchCriteria(event.target.value);
  };

  const setSearchCriteria = debounce((criteria: string) => {
    setCurrentPage(0);
    setSearchText(criteria);
  }, 400);

  const calculateAfterPagination = (): string | null => {
    const afterNumber = currentPage * rowsPerPage - 1;
    return afterNumber <= 0 ? null : afterNumber.toString();
  };

  const updateSelectedPill = (newPill: PillIds) => {
    setActivePill(newPill);
    setCurrentPage(0);
  };

  const getCustomFilters = (): AGFCustomFilters => {
    const cFilters: AGFCustomFilters = {
      creditLead: {
        pendingBackgroundCheck: activePill === PillIds.BACKGROUND_CHECK_PENDING,
        sent: activePill === PillIds.BACKGROUND_CHECK_SENT,
        generateDraft: activePill === PillIds.CREDIT_LEAD_CPR_GENERATION,
        waitingDocuments: activePill === PillIds.CREDIT_LEAD_CPR_COMPLETED,
        reviewingDocuments: activePill === PillIds.CREDIT_LEAD_CPR_VALIDATION,
        documentsValidated: activePill === PillIds.CREDIT_LEAD_CPR_ASSIGNATION,
        pendingApproval: activePill === PillIds.CREDIT_ANALYSIS_PENDING,
        onHold: activeTabId === TabIds.CREDIT_LEAD_REPORT_COMMERCIAL,
        rejected: activeTabId === TabIds.CREDIT_LEAD_REJECTED
      },
      fieldMonitor: {
        completed: isTerramagna ? true : activePill === PillIds.TERRAMAGNA_COMPLETED
      }
    };
    return cFilters;
  };

  const renderPendingNegotiations = ({ data }: { data: { financingAgFinancingQueue: AgFinancingConnection } }) => {
    const nodes: AgFinancing[] = data?.financingAgFinancingQueue?.edges.map(n => n.node);
    const pagination = {
      limit: rowsPerPage,
      offset: currentPage * rowsPerPage,
      total: data?.financingAgFinancingQueue?.totalCount || 0
    };

    const handleTableAction = (action: any) => {
      if (action.type === 'NEXT_PAGE') {
        setCurrentPage(currentPage + 1);
      } else if (action.type === 'PREVIOUS_PAGE') {
        setCurrentPage(currentPage - 1);
      }
    };

    const negotiationsList: FormattedReport[] = nodes.map(negotiation => {
      const cleanCpfCnpj = negotiation.grower?.cpfOrCnpj ? brazil.cleanCpfCnpjValue(negotiation.grower.cpfOrCnpj) : '';

      return {
        id: negotiation.id || '',
        name: negotiation.grower?.name || '',
        date: negotiation.phase1?.updatedAt,
        state: negotiation.grower?.state?.name || '',
        cpfOrCnpj: cleanCpfCnpj?.length === 14 ? brazil.cnpjInputMask(cleanCpfCnpj) : brazil.cpfInputMask(cleanCpfCnpj),
        salesPerson: '-',
        campaign: negotiation.campaign?.name || ''
      };
    });

    return (
      <Table
        label="Reports"
        rowDefinition={rowDefinition}
        columnDefinition={columnDefinition}
        data={negotiationsList}
        pagination={pagination}
        paginationStyle="prev-next"
        onTableAction={handleTableAction}
        currentColumns={getCurrentColumns(isTerramagna)}
      />
    );
  };
  const onTabChange = (selectedTab: TabIds) => {
    setActiveTab(selectedTab);
    setCurrentPage(0);
    switch (selectedTab) {
      case TabIds.CREDIT_LEAD_REPORT_INITIAL_ANALYSIS:
        setActivePill(PillIds.BACKGROUND_CHECK_PENDING);
        break;
      case TabIds.CREDIT_LEAD_FORMALIZATIONS:
        setActivePill(PillIds.CREDIT_LEAD_CPR_GENERATION);
        break;
      case TabIds.CREDIT_LEAD_MONITORING:
        setActivePill(PillIds.TERRAMAGNA_COMPLETED);
        break;
      default:
        setActivePill(null);
        break;
    }
  };

  return (
    <NewPage
      title={isTerramagna ? text.toTitleCase(i18next.t('georeferencingReport')) : i18next.t('creditLeadReports')}
    >
      <div className="flex mb32">
        <div>
          {!isTerramagna ? (
            <Tabs
              activeTabId={activeTabId}
              ariaLabel="Credit lead info"
              onChange={onTabChange}
              items={TABS_CONFIG.creditLeadReport}
              renderPanel={tabId => {
                return (
                  <div className={classes.pillsLayout}>
                    <Pills
                      activePillId={activePill || PillIds.BACKGROUND_CHECK_PENDING}
                      ariaLabel="Credit lead report filter pills"
                      items={PILLS_CONFIG[tabId] || []}
                      onChange={updateSelectedPill}
                    />
                  </div>
                );
              }}
            />
          ) : null}
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <div style={{ width: 350 }}>
            <Input
              label={i18next.t('search')}
              hideLabel
              faIconBefore={faSearch}
              placeholder={i18next.t('search')}
              onChange={handleSearch}
              sizeVariant="slim"
              defaultValue={searchText}
            />
          </div>
        </div>
      </div>
      <LoadingWrapper
        query={financingAgFinancingQueue}
        queryOptions={{
          variables: {
            pagination: { first: rowsPerPage, after: calculateAfterPagination() },
            filters: { query: searchText },
            customFilters: getCustomFilters()
          },
          fetchPolicy: 'no-cache'
        }}
      >
        {renderPendingNegotiations}
      </LoadingWrapper>
    </NewPage>
  );
};
